import { Comment } from '@common/schemas/comment.schema';
import {
  CheckCircleOutline,
  Close,
  DeleteOutline
} from '@mui/icons-material';
import {
  Box,
  Button,
  Divider,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface CommentPopoverProps {
  comment?: Comment;
  onSubmit?: (content: string) => void;
  onResolve?: (id: string) => void;
  onDelete?: (id: string) => void;
  onClose: () => void;
  isNew?: boolean;
}

/**
 * UI component for viewing and creating comments.
 */
export function CommentPopover({
  comment,
  onSubmit,
  onResolve,
  onDelete,
  onClose,
  isNew = false
}: CommentPopoverProps) {
  const { t } = useTranslation();
  const [content, setContent] = useState(comment?.content || '');

  const handleSubmit = () => {
    if (content.trim() && onSubmit) {
      onSubmit(content);
    }
  };

  return (
    <Paper
      elevation={8}
      sx={{
        width: 300,
        p: 2,
        borderRadius: 2,
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        position: 'relative',
        boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
      }}
    >
      <Stack spacing={2}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'primary.main' }}>
            {isNew ? t('editor.add_comment') : comment?.authorName}
          </Typography>
          <IconButton size="small" onClick={onClose} sx={{ opacity: 0.6 }}>
            <Close fontSize="small" />
          </IconButton>
        </Box>

        {isNew ? (
          <>
            <TextField
              fullWidth
              multiline
              rows={3}
              variant="outlined"
              placeholder={t('editor.comment_placeholder')}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              autoFocus
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'action.hover'
                }
              }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button
                size="small"
                onClick={onClose}
                sx={{ color: 'text.secondary' }}
              >
                {t('common.cancel')}
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={handleSubmit}
                disabled={!content.trim()}
                sx={{ fontWeight: 700 }}
              >
                {t('common.save')}
              </Button>
            </Box>
          </>
        ) : (
          <>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: 'text.primary', lineHeight: 1.6 }}>
              {comment?.content}
            </Typography>
            <Divider sx={{ opacity: 0.5 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="caption" sx={{ color: 'text.disabled', fontWeight: 600 }}>
                {comment?.audit.createdAt && (
                  new Date(comment.audit.createdAt).toLocaleDateString()
                )}
              </Typography>
              <Stack direction="row" spacing={0.5}>
                {onResolve && comment && (
                  <IconButton
                    size="small"
                    onClick={() => onResolve(comment.id)}
                    title={t('editor.resolve_comment')}
                    sx={{ color: 'success.main', opacity: 0.7, '&:hover': { opacity: 1 } }}
                  >
                    <CheckCircleOutline fontSize="small" />
                  </IconButton>
                )}
                {onDelete && comment && (
                  <IconButton
                    size="small"
                    onClick={() => onDelete(comment.id)}
                    title={t('common.delete')}
                    sx={{ color: 'error.main', opacity: 0.7, '&:hover': { opacity: 1 } }}
                  >
                    <DeleteOutline fontSize="small" />
                  </IconButton>
                )}
              </Stack>
            </Box>
          </>
        )}
      </Stack>
    </Paper>
  );
}
